import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import '../../styles/zoneMonitoring.css';
import '../../styles/actions.css';
import '../../styles/automation.css';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import MqttChart from "../mqtt/MqttChart";
import { fetchHistoricalSensorData, useMQTTSensorData, parseAmbientData} from "../../utils/mqttUtils";
import { useCardData, useCardContext } from "../common/CardContext";
import ChartSettings from "../cards/ChartSettings";
import Modal from "../common/Modal";
import ZoneActionsBoard from "../actions/ZoneActionsBoard";
import AutomationSummary from "../actions/AutomationSummary";

import ChatBot from "../common/ChatBot";
import chatIcon from "../../assets/images/chat.png";
import "../../styles/chatbot.css";

import { useCard } from "../../utils/useDashboardHooks";
import InlineRename from "../common/InlineRename";
import { mqttTimeRangeOptions } from "../../utils/constants";
import { takeThresholdSnapshot, compareSnapshots } from "../../utils/zoneUtils";
import { useUpdateCardMutation, getUserValue, useCards } from "../../utils/reactQueryHooks";
import { useChatUIPersistence } from "../common/ChatUIPersistenceContext";

// Separate component for zone vitals to isolate real-time updates
const ZoneVitals = React.memo(({ latestData, sensorCard, historicalData }) => {
    if (!latestData) return null;
    
    // Check if data is loading (values are null/undefined)
    const isLoadingHumidity = latestData.mostRecentHumidity === null || latestData.mostRecentHumidity === undefined;
    const isLoadingTemp = latestData.mostRecentTemp === null || latestData.mostRecentTemp === undefined;
    const isLoadingWater = !historicalData?.distance?.[0]?.value;
    
    return (
        <div className="zone-vitals-summary">
            <h3 className="vitals-title">Zone Vitals</h3>
            
            <div className="vital-item">
                <span className="vital-icon">💧</span>
                <span className="vital-label">Humidity:</span>
                <span 
                    className="vital-value" 
                    style={{ color: isLoadingHumidity ? 'rgba(255, 255, 255, 0.4)' : 'inherit' }}
                >
                    {isLoadingHumidity ? '--' : `${latestData.mostRecentHumidity?.toFixed(1) ?? 0.00}%`}
                </span>
                {!isLoadingHumidity && sensorCard?.humidityHigh && latestData.mostRecentHumidity > sensorCard.humidityHigh && <span className="vital-alert high">↑</span>}
                {!isLoadingHumidity && sensorCard?.humidityLow && latestData.mostRecentHumidity < sensorCard.humidityLow && <span className="vital-alert low">↓</span>}
            </div>
            
            <div className="vital-item">
                <span className="vital-icon">🌡️</span>
                <span className="vital-label">Temp:</span>
                <span 
                    className="vital-value" 
                    style={{ color: isLoadingTemp ? 'rgba(255, 255, 255, 0.4)' : 'inherit' }}
                >
                    {isLoadingTemp ? '--' : `${latestData.mostRecentTemp?.toFixed(1) ?? 0.000}°F`}
                </span>
                {!isLoadingTemp && sensorCard?.temperatureHigh && latestData.mostRecentTemp > sensorCard.temperatureHigh && <span className="vital-alert high">↑</span>}
                {!isLoadingTemp && sensorCard?.temperatureLow && latestData.mostRecentTemp < sensorCard.temperatureLow && <span className="vital-alert low">↓</span>}
            </div>
            
            <div className="vital-item">
                <span className="vital-icon">📏</span>
                <span className="vital-label">Water Level:</span>
                <span 
                    className="vital-value" 
                    style={{ color: isLoadingWater ? 'rgba(255, 255, 255, 0.4)' : 'inherit' }}
                >
                    {isLoadingWater ? '--' : `${historicalData?.distance?.[0]?.value?.toFixed(2) ?? 0.00} in`}
                </span>
                {!isLoadingWater && sensorCard?.waterLow && historicalData?.distance?.[0]?.value < sensorCard.waterLow && <span className="vital-alert low">↓</span>}
            </div>
            
        </div>
    );
});

ZoneVitals.displayName = 'ZoneVitals';

ZoneVitals.propTypes = {
    latestData: PropTypes.shape({
        mostRecentHumidity: PropTypes.number,
        mostRecentTemp: PropTypes.number,
        mostRecentTimestamp: PropTypes.string
    }),
    sensorCard: PropTypes.shape({
        humidityHigh: PropTypes.number,
        humidityLow: PropTypes.number,
        temperatureHigh: PropTypes.number,
        temperatureLow: PropTypes.number,
        waterLow: PropTypes.number
    }),
    historicalData: PropTypes.object
};

export default function MqttWrapper() {
    const { macAddress } = useParams();
    const navigate = useNavigate();
    const columns = ["humidity", "temperature", "distance", "lux"];
    
    // Handle back navigation
    const handleBack = () => {
        navigate(-1);
    };
    
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [selectedTimeRange, setSelectedTimeRange] = useState("1d");
    
    // Chatbot state
    const { isChatModalOpen, setIsChatModalOpen } = useChatUIPersistence();
    


    // Set up React Query client
    const queryClient = useQueryClient();
    const updateOneCardMutation = useUpdateCardMutation();
    
    // Use React Query to fetch cards data (same pattern as ActionWrapper)
    const { data: cards = [], dataUpdatedAt } = useCards();
    const sensorCard = useMemo(() => 
        macAddress ? cards.find(card => card.macAddress === macAddress) || null : null,
    [cards, macAddress]);
    
    // Debug sensor card updates
    useEffect(() => {
        if (sensorCard) {
            
        }
    }, [sensorCard, dataUpdatedAt]);

    const { cardData } = useCardData();

    const latestData = cardData[macAddress] ? cardData[macAddress] : null;
    const currentTimestamp = latestData?.mostRecentTimestamp ?? null;

    const [historicalData, setHistoricalData] = useState([]);

    const openSettings = useCallback(() => setIsSettingsOpen(true), []);
    const closeSettings = useCallback(() => setIsSettingsOpen(false), []);
    
    // ChatBot functions
    const openChatBot = useCallback(() => setIsChatModalOpen(prev => !prev), [setIsChatModalOpen]);
    const closeChatBot = useCallback(() => setIsChatModalOpen(false), [setIsChatModalOpen]);
    
    // Robust update handler that ensures persistence across all data layers
    const handleCardUpdate = useCallback(async (newCardData) => {

        
        // React Query will automatically update the sensorCard state
        // Take snapshots; do NOT close the modal here so the user can review
        // the threshold update result. Settings close when user dismisses that modal.
        takeThresholdSnapshot(newCardData, 'AFTER_UPDATE');
        compareSnapshots();
        }, []
    );

    // Memoize chart props to avoid unnecessary re-renders
    const chartProps = useMemo(() => ({
        backgroundColor: "transparent",
        dataParser: parseAmbientData,
        thresholds: {
            humidity: {
                low: sensorCard?.humidityLow ? parseFloat(sensorCard.humidityLow) : null, 
                high: sensorCard?.humidityHigh ? parseFloat(sensorCard.humidityHigh) : null
            },
            temperature: {
                low: sensorCard?.temperatureLow ? parseFloat(sensorCard.temperatureLow) : null, 
                high: sensorCard?.temperatureHigh ? parseFloat(sensorCard.temperatureHigh) : null
            },
            distance: {
                low: sensorCard?.waterLow ? parseFloat(sensorCard.waterLow) : null, 
                high: sensorCard?.waterHigh ? parseFloat(sensorCard.waterHigh) : null
            },
            lux: {
                low: sensorCard?.lightLow || null,
                high: sensorCard?.lightHigh || null
            }
        }
    }), [sensorCard?.humidityLow, sensorCard?.humidityHigh, sensorCard?.temperatureLow, sensorCard?.temperatureHigh, sensorCard?.waterLow, sensorCard?.waterHigh, sensorCard?.lightLow, sensorCard?.lightHigh]);

    // Memoize columns array to prevent unnecessary re-fetches
    const stableColumns = useMemo(() => columns, [columns.join(',')]);
    
    // Memoize macAddress to prevent unnecessary re-fetches
    const stableMacAddress = useMemo(() => macAddress, [macAddress]);
    
    useEffect(() => {
        if (!stableMacAddress) return;
        
        // 🚨 DEBUG: Track MqttWrapper data fetching
        console.log('🚨 MqttWrapper: Fetching historical data:', {
            macAddress: stableMacAddress,
            selectedTimeRange,
            columns: stableColumns,
            timestamp: new Date().toISOString()
        });
        
        const fetchDataForTimeRange = async () => {
            try {
                const combinedData = {};
                for (const column of stableColumns) {
                    const data = await fetchHistoricalSensorData(stableMacAddress, column, selectedTimeRange, "sensor");
                    combinedData[column] = data;
                }
                setHistoricalData(combinedData);
            }
            catch (error) {
                console.error("Error loading historical data:", error);
            }
        };
        fetchDataForTimeRange();
    }, [selectedTimeRange, stableMacAddress, stableColumns]);

    // Only use MQTT for real-time updates to CardContext, not for historical data
    useMQTTSensorData(macAddress, null);

    return (
        <div className="mqtt-chart-container">

            <div className="zone-header">
                <button className="zone-control-btn" onClick={handleBack}>
                    <i className="ri-arrow-left-line"></i>
                    <span>Back</span>
                </button>
                <div className="zone-header-main">
                    <div className="zone-title-row">
                        <h2 className="zone-title">
                            <InlineRename
                                value={sensorCard?.sensorDescription || ''}
                                onSave={(next, callbacks) => {
                                    if (!sensorCard) return;
                                    updateOneCardMutation.mutate(
                                        { ...sensorCard, sensorDescription: next },
                                        callbacks
                                    );
                                }}
                            />
                        </h2>
                    </div>
                    <p className="zone-updated">Last Updated: {currentTimestamp}</p>
                </div>
                <div className="zone-controls">
                    <button className="zone-control-btn" onClick={openSettings} title="Edit Zone Settings">
                        <i className="ri-settings-3-line"></i>
                        <span>Settings</span>
                    </button>
                </div>
            </div>
            
            {/* Zone Vitals - isolated component */}
            <ZoneVitals 
                latestData={latestData}
                sensorCard={sensorCard}
                historicalData={historicalData}
            />
            
            {/* Zone Actions Board */}
            <ZoneActionsBoard sensorMacAddress={macAddress} />
            
            {/* Automation Summary */}
            <AutomationSummary sensorMacAddress={macAddress} />
            
            {/* ChatBot Button */}
            <button 
                className="chatbot-toggle-button" 
                onClick={openChatBot}
                aria-label="Open Chat Assistant"
                title="Chat with Canopy AI Assistant"
            >
                <img src={chatIcon} alt="Chat" className="chatbot-icon" />
            </button>
            
            {/* ChatBot Modal */}
            {isChatModalOpen && (
                <ChatBot 
                    isModal={true}
                    closeChatModal={closeChatBot}
                    modalPosition="bottom-right"
                    metaData={{
                        macAddress: macAddress, // Use this sensor's MAC
                        timeRange: selectedTimeRange // Use current time range
                    }}
                />
            )}
            
            <div className="chart-time-range-selector">
                {
                    mqttTimeRangeOptions.map((option) => {
                        return (
                            <button
                                key={option.value}
                                className={`time-range-button ${selectedTimeRange === option.value ? 'active' : ''}`}
                                onClick={() => setSelectedTimeRange(option.value)}
                            >
                                {option.label}
                            </button>
                        )
                    })
                }
            </div>
            <MqttChart 
                key={`humidity-${macAddress}`}
                sensorType="Humidity" 
                historicalData={historicalData.humidity}
                backgroundColor="transparent"
                borderColor="lightgreen"
                valueKey="humidity"
                {...chartProps}
                thresholds={chartProps.thresholds.humidity}
                selectedTimeRange={selectedTimeRange}
            />
            <br />
            <MqttChart 
                key={`temperature-${macAddress}`}
                sensorType="Temperature" 
                historicalData={historicalData.temperature}
                backgroundColor="transparent"
                borderColor="lightsalmon"
                valueKey="temperature"
                {...chartProps}
                thresholds={chartProps.thresholds.temperature}
                selectedTimeRange={selectedTimeRange}
            />
            <br />
            <br />
            <MqttChart 
                key={`distance-${macAddress}`}
                sensorType="Distance" 
                historicalData={historicalData.distance}
                backgroundColor="transparent"
                borderColor="lightblue"
                valueKey="distance"
                {...chartProps}
                thresholds={chartProps.thresholds.distance}
                selectedTimeRange={selectedTimeRange}
            />
            <br />
            <MqttChart 
                key={`lux-${macAddress}`}
                sensorType="Lux" 
                historicalData={historicalData.lux}
                backgroundColor="transparent"
                borderColor="yellow"
                valueKey="lux"
                {...chartProps}
                thresholds={chartProps.thresholds.lux}
                selectedTimeRange={selectedTimeRange}
            />
            <Modal isVisible={isSettingsOpen} onClose={closeSettings}>
                <ChartSettings
                    card={sensorCard}
                    onSave={handleCardUpdate}
                    onResultModalClose={closeSettings}
                />
            </Modal>
        </div>
    )
}